"use client";

import React, { useState } from 'react';
import Image from 'next/image';
import { CarCardData } from '@/shared/lib/car-cards-store';
import {Button} from '@/shared/components/ui/button';
import UniversalBookingModal from '@/shared/components/ui/universal-booking-modal';

interface CarCardProps {
    data: CarCardData;
    isSelected?: boolean;
}

export const CarCard: React.FC<CarCardProps> = ({ data, isSelected = false }) => {
    const [selectedOilBrand, setSelectedOilBrand] = useState(data.oilBrands[0]);
    const [airFilterChecked, setAirFilterChecked] = useState(data.initialAirFilter || false);
    const [cabinFilterChecked, setCabinFilterChecked] = useState(data.initialCabinFilter || false);
    const [isUniversalBookingOpen, setIsUniversalBookingOpen] = useState(false);

    // Функция для правильного склонения слова "литр"
    const getLitersWord = (value: number): string => {
        return value < 5 ? 'литра' : 'литров';
    };

    // Расчет итоговой цены
    const totalPrice = 
        data.filterPrice + 
        (data.oilPrices[selectedOilBrand] || 0) +
        (airFilterChecked ? data.airFilterPrice : 0) +
        (cabinFilterChecked ? data.cabinFilterPrice : 0);


    return (
        <>
            <div 
                className="w-[250px] sm:w-[330px] lg:w-[410px] h-full overflow-hidden bg-custom-dark-gray flex flex-col rounded-[20px] transition-all duration-300"
                style={isSelected ? {
                    boxShadow: '0 0 0 4px #FF6200',
                } : {}}
            >
            {/* Top section with car image */}
            <div className="relative w-full h-[280px] sm:h-[320px] lg:h-[380px] bg-white flex-shrink-0">
                <Image
                    src={data.image}
                    alt={data.carModel}
                    fill
                    className="object-contain"
                    draggable={false}
                    style={{
                        ...(data.imagePosition?.top && { top: data.imagePosition.top }),
                        ...(data.imagePosition?.bottom && { bottom: data.imagePosition.bottom }),
                        ...(data.imagePosition?.left && { left: data.imagePosition.left }),
                        ...(data.imagePosition?.right && { right: data.imagePosition.right }),
                        ...(data.imagePosition?.transform && { transform: data.imagePosition.transform }),
                    }}
                />
                <div className="absolute w-full text-center bg-custom-orange px-2 py-2">
                    <span className="text-white text-[24px] sm:text-[20px] md:text-[24px] lg:text-[30px] uppercase">
                        {data.carModel}
                    </span>
                </div>
            </div>

            {/* Bottom section with details */}
            <div className="p-3 sm:p-4 lg:p-6 flex-1 bg-custom-lightgray flex flex-col overflow-y-auto">
                {/* Price */}
                <div className="text-custom-orange text-[24px] md:text-[36px] mb-1 lg:mb-2">
                    {totalPrice.toLocaleString('ru-RU')} ₽
                </div>

                {/* Car model description */}
                <div className="text-white text-[18px] sm:text-[18px] lg:text-[24px] mb-1 lg:mb-2">
                    {data.carModel} {data.year} ({data.fuelType}, {data.engineVolume})
                </div>

                {/* Service details */}
                <div className="text-white text-[12px] sm:text-[12px] lg:text-[16px] mb-2 lg:mb-4">
                    Фильтр VIC {data.filterPrice} руб., масло [{data.oilType}] {data.oilLiters} {getLitersWord(data.oilLiters)}
                </div>

                {/* Oil brand selection */}
                <div className="mb-2 lg:mb-4 mt-2">
                    <div className="text-white text-[12px] lg:text-[16px] mb-2">Бренд масла</div>
                    <div className="flex gap-1 sm:gap-2">
                        {data.oilBrands.map((brand: string) => (
                            <button
                                key={brand}
                                onClick={() => setSelectedOilBrand(brand)}
                                className={`cursor-pointer px-2 sm:px-3 py-1 sm:py-2 text-[12px] lg:text-[16px] transition-colors bg-white rounded-[5px] hover:border-custom-gray ${
                                    selectedOilBrand === brand
                                        ? 'text-custom-black border border-custom-orange hover:bg-opacity-80'
                                        : 'text-custom-black border border-custom-slight-gray hover:border-custom-gray'
                                }`}
                            >
                                {brand}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Additional options */}
                <div className="mb-3 lg:mb-6 mt-4">
                    <div className="text-white text-[12px] lg:text-[16px] mb-2">Дополнительные опции</div>
                    <div className="space-y-2">
                        <label className="flex items-center text-white text-[12px] lg:text-[16px] cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={airFilterChecked}
                                onChange={(e) => setAirFilterChecked(e.target.checked)}
                                className="mr-2 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 cursor-pointer accent-custom-gray border-custom-light rounded focus:ring-custom-gray hover:scale-110 transition-transform"
                            />
                            Воздушный фильтр
                        </label>
                        <label className="flex items-center text-white text-[12px] lg:text-[16px] cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={cabinFilterChecked}
                                onChange={(e) => setCabinFilterChecked(e.target.checked)}
                                className="mr-2 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 cursor-pointer accent-custom-gray border-custom-light rounded focus:ring-custom-gray hover:scale-110 transition-transform"
                            />
                            Салонный фильтр
                        </label>
                    </div>
                </div>

                {/* Book now button */}
                <div className="mt-auto">
                    <Button
                        variant="outlineOrange"
                        className="w-full bg-custom-dark font-button uppercase py-3 px-5"
                        onClick={() => setIsUniversalBookingOpen(true)}
                    >
                        ЗАПИСАТЬСЯ
                    </Button>
                </div>
            </div>
        </div>
        <UniversalBookingModal isOpen={isUniversalBookingOpen} onClose={() => setIsUniversalBookingOpen(false)} blockName={`Карточка автомобиля - ${data.carModel}`}/>
        </>
    );
};

export default CarCard;